import { useState, useRef, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Search } from "lucide-react";
import { API } from "@/apiConfig";

export default function LookupDialog({ open, onOpenChange, products, onAddToCart }) {
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQuery("");
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  const filtered = query.length >= 4
    ? (() => {
        const q = query.toLowerCase();
        const sw = products.filter(p => p.name?.toLowerCase().startsWith(q));
        const swIds = new Set(sw.map(p => p.id));
        const ct = products.filter(p => !swIds.has(p.id) && p.name?.toLowerCase().includes(q));
        return [...sw, ...ct].slice(0, 100);
      })()
    : [];

  const handleSelect = (product) => {
    onAddToCart(product);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Search className="w-5 h-5" />
            Product Lookup
          </DialogTitle>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <Input
            ref={inputRef}
            placeholder="Type product name (min 4 characters)..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-10 h-11 text-base"
            data-testid="lookup-search-input"
          />
        </div>

        {query.length > 0 && query.length < 4 && (
          <p className="text-sm text-zinc-400 text-center py-2">Type at least 4 characters to search</p>
        )}

        {query.length >= 4 && filtered.length === 0 && (
          <p className="text-sm text-zinc-500 text-center py-4">No products found</p>
        )}

        {filtered.length > 0 && (
          <ScrollArea className="max-h-[500px]">
            <div className="space-y-1">
              {filtered.map((product) => (
                <button
                  key={product.id}
                  onClick={() => handleSelect(product)}
                  className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-blue-50 border border-transparent hover:border-blue-200 transition-all text-left"
                  data-testid={`lookup-item-${product.sku}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-zinc-900 truncate">{product.name}</p>
                    <p className="text-xs text-zinc-400 font-mono">{product.sku}</p>
                  </div>
                  <span className="text-base font-bold text-blue-600 ml-3 font-mono">
                    ${(product.retail_price || 0).toFixed(2)}
                  </span>
                </button>
              ))}
            </div>
          </ScrollArea>
        )}

        <p className="text-xs text-zinc-400 text-right">
          {filtered.length > 0 ? `${filtered.length} results` : ""}
        </p>
      </DialogContent>
    </Dialog>
  );
}
